import { Link } from 'react-router-dom';

import type { InboundDraft } from '@/modules/jobs/signal/types';
import { Switch } from '@/modules/ui/components/switch/Switch';
import { Textarea } from '@/modules/ui/components/textarea/Textarea';

interface JobInboundFieldsProps {
  value: InboundDraft;
  onChange: (value: InboundDraft) => void;
  isCareersPageEnabled: boolean;
  /** Where the Careers Page is switched on (the Workspace page). */
  workspacePath: string;
}

/**
 * The self-service editor's applications controls: whether the job accepts applications from the Careers Page, and
 * the description applicants read. A job can only be switched on while the Careers Page is on; one that is already on
 * keeps its setting while the page is off, so the switch stays usable to turn it off.
 */
export function JobInboundFields({
  value,
  onChange,
  isCareersPageEnabled,
  workspacePath,
}: JobInboundFieldsProps) {
  const canTurnOn = isCareersPageEnabled || value.isInboundEnabled;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <label htmlFor="job-inbound" className="font-medium">
            Accept applications
          </label>
          <p className="text-muted-foreground text-[13px]">
            {isCareersPageEnabled
              ? 'List this job on your Careers Page while it is published, so people can apply.'
              : 'Applications come in through your Careers Page, which is off.'}
          </p>
          {!isCareersPageEnabled && (
            <p className="mt-1 text-[13px]">
              <Link
                to={workspacePath}
                className="text-primary-700 font-medium hover:underline"
              >
                Turn on your Careers Page in Workspace first
              </Link>
            </p>
          )}
        </div>
        <Switch
          id="job-inbound"
          className="mt-0.5 shrink-0"
          checked={value.isInboundEnabled}
          disabled={!canTurnOn}
          onCheckedChange={(isInboundEnabled) =>
            onChange({ ...value, isInboundEnabled })
          }
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="job-public-description" className="font-medium">
          Description for applicants
          <span className="ml-1.5 text-[13px] font-normal text-neutral-500">
            Optional
          </span>
        </label>
        <p className="text-muted-foreground -mt-1 text-[13px]">
          Shown on your Careers Page. Without it, applicants see only the title,
          location and contract details. Your job description above stays
          private.
        </p>
        <Textarea
          id="job-public-description"
          rows={5}
          maxLength={10_000}
          placeholder="What makes this role worth applying for…"
          value={value.publicJobDescription}
          onChange={(e) =>
            onChange({ ...value, publicJobDescription: e.target.value })
          }
        />
      </div>
    </div>
  );
}
